import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Typography,
    TextField,
    Card,
    CardContent,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useCart } from '../context/CartContext';

const WHATSAPP_PHONE = '221710338061'; // Numéro WhatsApp Sénégal sans symboles
const WHATSAPP_MESSAGE_LINK = `https://wa.me/${WHATSAPP_PHONE}`;

const CartPage: React.FC = () => {
    const navigate = useNavigate();
    const { cartItems, removeFromCart, updateQuantity, getTotalPrice, clearCart } = useCart();

    const handleGenerateWhatsAppMessage = () => {
        if (cartItems.length === 0) {
            alert('Votre panier est vide !');
            return;
        }

        let message = `🎁 Bonjour, je voudrais commander:\n\n`;

        cartItems.forEach((item) => {
            const price = item.parfum.price || 0;
            message += `✨ ${item.parfum.name}\n`;
            message += `   Quantité: ${item.quantity}\n`;
            message += `   Prix unitaire: ${price.toLocaleString('fr-TN')} TND\n`;
            message += `   Sous-total: ${(price * item.quantity).toLocaleString('fr-TN')} TND\n\n`;
        });

        const total = getTotalPrice();
        message += `💰 TOTAL: ${total.toLocaleString('fr-TN')} TND\n\n`;
        message += `Merci!`;

        const encodedMessage = encodeURIComponent(message);
        window.open(`${WHATSAPP_MESSAGE_LINK}?text=${encodedMessage}`, '_blank');
    };

    if (cartItems.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, minHeight: '70vh' }}>
                <Paper elevation={0} sx={{ textAlign: 'center', py: 6, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="h4" color="textSecondary" gutterBottom>
                        Votre panier est vide
                    </Typography>
                    <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                        Découvrez nos magnifiques parfums
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/parfums')}
                        sx={{
                            backgroundColor: '#8B4513',
                            '&:hover': { backgroundColor: '#A0522D' },
                        }}
                    >
                        Continuer shopping
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 'bold', color: '#333' }}>
                🛍️ Mon Panier
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
                {/* Tableau des produits */}
                <Box>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Produit</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                        Prix
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                        Quantité
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                        Sous-total
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cartItems.map((item) => (
                                    <TableRow key={item.parfum.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <img
                                                    src={item.parfum.image}
                                                    alt={item.parfum.name}
                                                    style={{
                                                        width: 60,
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                    }}
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).src = '/images/placeholder.txt';
                                                    }}
                                                />
                                                <Typography>{item.parfum.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            {(item.parfum.price || 0).toLocaleString('fr-TN')} Fr
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => {
                                                    const value = parseInt(e.target.value) || 1;
                                                    updateQuantity(item.parfum.id, value);
                                                }}
                                                inputProps={{ min: 1, max: 100 }}
                                                size="small"
                                                sx={{ width: 80 }}
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                            {((item.parfum.price || 0) * item.quantity).toLocaleString('fr-TN')} FrCFA
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                color="error"
                                                size="small"
                                                onClick={() => removeFromCart(item.parfum.id)}
                                                startIcon={<DeleteIcon />}
                                            >
                                                Supprimer
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                {/* Résumé du panier */}
                <Box>
                    <Card elevation={3} sx={{ backgroundColor: '#f9f9f9', position: 'sticky', top: 20 }}>
                        <CardContent>
                            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                                Résumé du panier
                            </Typography>

                            <Box sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                        pb: 1,
                                        borderBottom: '1px solid #ddd',
                                    }}
                                >
                                    <Typography variant="body1">Nombre d'articles:</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                    <Typography variant="h6">Total:</Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: '#8B4513',
                                            fontSize: '1.5rem',
                                        }}
                                    >
                                        {getTotalPrice().toLocaleString('fr-TN')} TND
                                    </Typography>
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                startIcon={<WhatsAppIcon />}
                                onClick={handleGenerateWhatsAppMessage}
                                sx={{
                                    backgroundColor: '#25D366',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    mb: 2,
                                    '&:hover': {
                                        backgroundColor: '#1fa857',
                                    },
                                }}
                            >
                                Commander via WhatsApp
                            </Button>

                            <Button
                                variant="outlined"
                                fullWidth
                                size="small"
                                onClick={() => navigate('/parfums')}
                                sx={{ mb: 1 }}
                            >
                                Continuer shopping
                            </Button>

                            <Button
                                variant="text"
                                fullWidth
                                size="small"
                                onClick={clearCart}
                                sx={{ color: '#d32f2f' }}
                            >
                                Vider le panier
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Container>
    );
};

export default CartPage;
